import { memo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { AlertOctagon, AlertTriangle, BookOpen, Brain, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { showSuccessToast } from '@/components/NotificationToast';
import type { MessageStep } from '../scenarios';

interface AssistantMessageProps {
  step: MessageStep;
  /** Streamed prefix of step.content; equals full content once done. */
  streamedContent: string;
  isStreaming: boolean;
  time?: string;
}

const confidenceStyles: Record<string, string> = {
  висока: 'bg-green-50 text-green-700',
  средна: 'bg-amber-50 text-amber-700',
  ниска: 'bg-red-50 text-red-700',
};

export const AssistantMessage = memo(function AssistantMessage({
  step,
  streamedContent,
  isStreaming,
  time,
}: AssistantMessageProps): JSX.Element {
  const [clicked, setClicked] = useState<Set<string>>(new Set());

  const handleAction = (label: string) => {
    setClicked((prev) => new Set(prev).add(label));
    showSuccessToast(`„${label}“ — отбелязано (демо)`);
  };

  return (
    <div className="flex w-full justify-start gap-2.5">
      <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-100">
        <Brain className="h-4 w-4 text-neutral-500" />
      </span>
      <div className="max-w-[80%] space-y-2">
        <div className="rounded-2xl rounded-tl-md bg-neutral-100 px-4 py-3">
          <div
            className="max-w-none break-words text-sm leading-relaxed text-neutral-900"
            aria-live="polite"
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
                ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>,
                ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>,
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                hr: () => <hr className="my-3 border-neutral-200" />,
                blockquote: ({ children }) => (
                  <blockquote className="my-2 border-l-2 border-neutral-300 pl-3 italic text-neutral-600">
                    {children}
                  </blockquote>
                ),
                code: ({ children }) => (
                  <code className="rounded bg-white px-1 py-0.5 text-xs">{children}</code>
                ),
              }}
            >
              {streamedContent}
            </ReactMarkdown>
          </div>

          {!isStreaming && (step.citations?.length || step.confidence) && (
            <div className="mt-3 space-y-1 border-t border-neutral-200/70 pt-2">
              {step.citations?.map((citation, i) => (
                <p key={i} className="flex items-start gap-1.5 text-xs text-neutral-500">
                  <BookOpen className="mt-0.5 h-3 w-3 shrink-0 text-neutral-400" />
                  <span>
                    Източник: <span className="font-medium text-neutral-600">{citation.label}</span> —{' '}
                    {citation.source}
                    {citation.detail && ` (${citation.detail})`}
                  </span>
                </p>
              ))}
              {step.confidence && (
                <span
                  className={cn(
                    'inline-block rounded px-1.5 py-0.5 text-[11px]',
                    confidenceStyles[step.confidence]
                  )}
                >
                  увереност: {step.confidence}
                </span>
              )}
            </div>
          )}
        </div>

        {!isStreaming && step.contradictions && step.contradictions.length > 0 && (
          <div className="space-y-1.5">
            {step.contradictions.map((c, i) => (
              <div
                key={i}
                className={cn(
                  'flex items-start gap-2 rounded-lg border-l-2 px-3 py-2 text-xs',
                  c.severity === 'hard'
                    ? 'border-red-400 bg-red-50 text-red-800'
                    : 'border-amber-400 bg-amber-50 text-amber-800'
                )}
              >
                {c.severity === 'hard' ? (
                  <AlertOctagon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                ) : (
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                )}
                <div className="flex-1">
                  <p>{c.text}</p>
                  {c.status === 'unresolved' && (
                    <span className="mt-1 inline-block rounded border border-current/30 px-1.5 py-0.5 text-[11px] font-medium">
                      Неразрешено
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {!isStreaming && step.approvalActions && step.approvalActions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {step.approvalActions.map((action) => (
              <Button
                key={action.label}
                size="sm"
                variant={action.variant === 'secondary' ? 'outlineAccent' : 'blue'}
                disabled={clicked.has(action.label)}
                onClick={() => handleAction(action.label)}
              >
                {clicked.has(action.label) && <Check className="mr-1 h-3.5 w-3.5" />}
                {action.label}
              </Button>
            ))}
          </div>
        )}

        {!isStreaming && time && <p className="px-1 text-[11px] text-neutral-400">{time}</p>}
      </div>
    </div>
  );
});
